import { useCallback, useEffect, useRef, useState } from "react";
import { api, post } from "./api.js";
import {Eye,EyeOff,ShieldCheck} from 'lucide-react';
import {DEFAULT_MARTINGALES,MAX_MARTINGALES,isValidMartingaleCount,martingaleAmount} from '../shared/martingale.js';
import {explainIndependentScore} from './independentScore.js';
import TradeReviewPanel from './TradeReviewPanel.jsx';
import {BASELINE_POLICY,EMA_CLOCK_POLICY,SELECTIVE_GATE_LABELS,isValidLossLimit} from '../shared/signal-policy.js';

const phases = {
  idle: "Parado",
  scanning: "Buscando oportunidade",
  searching: "Buscando oportunidade",
  waiting_scan: "Aguardando próxima busca",
  search_scheduled: "Aguardando próxima busca",
  waiting_entry: "Aguardando entrada",
  scheduled: "Entrada agendada",
  ordering: "Enviando ordem Demo",
  submitting: "Enviando ordem Demo",
  preparing: "Preparando entrada Demo",
  open: "Ordem aberta · aguardando liquidação",
  unknown: "Estado da ordem não confirmado · novas entradas bloqueadas",
  expired_unknown: "Envio incerto expirado · preservado no histórico",
  paused: "Ciclo pausado",
  paused_restart: "Ciclo pausado após reinício",
  paused_loss_limit:"Limite de perdas atingido",
  waiting_result: "Aguardando liquidação",
  settling: "Aguardando liquidação",
  waiting_gale: "Aguardando G1",
  stopped: "Parado",
  manual_ready: "Sinal aguardando sua confirmação",
  cancelled: "Cancelada antes do envio",
  settled: "Liquidação confirmada",
  stopping: "Parada solicitada",
  error: "Ciclo interrompido",
};
const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  dateStyle: "short",
  timeStyle: "medium",
});
const dollarFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
});

function dateTime(value) {
  if (value == null || value === "") return "—";
  const milliseconds = typeof value === "number" && value < 1e12 ? value * 1000 : value;
  const date = new Date(milliseconds);
  return Number.isFinite(date.getTime()) ? `${dateFormatter.format(date)} · Brasília` : "Horário não confirmado";
}
function metric(value, suffix = "") {
  return typeof value === "number" && Number.isFinite(value)
    ? `${value.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}${suffix}`
    : "—";
}

export default function IndependentAutoPanel({onStatus}) {
  const [status, setStatus] = useState(null);
  const [ssid, setSsid] = useState("");
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [showPassword,setShowPassword]=useState(false);
  const [featureIdentity,setFeatureIdentity]=useState('');
  const [amount, setAmount] = useState("5");
  const [maxGale, setMaxGale] = useState(()=>{
    const saved=localStorage.getItem('trader.generator.maxGale');
    return saved!==null&&isValidMartingaleCount(Number(saved))?Number(saved):DEFAULT_MARTINGALES;
  });
  const [mode, setMode] = useState(() => localStorage.getItem("trader.generator.mode") === "manual" ? "manual" : "automatic");
  const signalPolicy=EMA_CLOCK_POLICY;
  const [lossLimit,setLossLimit]=useState(()=>{const saved=Number(localStorage.getItem('trader.generator.lossLimit')??0);return isValidLossLimit(saved)?saved:0;});
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pollError, setPollError] = useState("");
  const mounted = useRef(false);
  const fetching = useRef(false);
  const mutating = useRef(false);
  const controller = useRef(null);

  const refresh = useCallback(async (afterAction = false) => {
    if (fetching.current || (mutating.current && !afterAction)) return;
    fetching.current = true;
    const request = new AbortController();
    controller.current = request;
    try {
      const next = await api("/independent/status", { signal: request.signal });
      if (mounted.current) {
        setStatus(next);
        setCurrentTime(Date.now());
        if (next.auto?.running && next.auto.config?.mode) setMode(next.auto.config.mode);
        onStatus?.(next);
        setPollError("");
      }
    } catch (err) {
      if (mounted.current && err.name !== "AbortError") setPollError(err.message);
    } finally {
      fetching.current = false;
      if (controller.current === request) controller.current = null;
    }
  }, [onStatus]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const timer = setInterval(() => refresh(), 2000);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      controller.current?.abort();
    };
  }, [refresh]);

  const perform = async (operation) => {
    if (mutating.current) return;
    mutating.current = true;
    setBusy(true);
    setError("");
    try {
      await operation();
      await refresh(true);
    } catch (err) {
      if (mounted.current) setError(err.message);
    } finally {
      mutating.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const connect = (event) => {
    event.preventDefault();
    const session = ssid.trim();
    if (!session) return;
    perform(async () => {
      try {
        await post("/independent/connect", { ssid: session,...(featureIdentity.trim()?{feature_identity:featureIdentity.trim()}:{}) });
      } finally {
        if (mounted.current) {setSsid("");setFeatureIdentity('');}
      }
    });
  };
  const login = event => {
    event.preventDefault();
    if (!email.trim() || !password) return;
    perform(async () => {
      try { await post('/independent/login',{email:email.trim(),password}); }
      finally { if(mounted.current)setPassword(''); }
    });
  };
  const start = (event) => {
    event.preventDefault();
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 5) {
      setError("Informe um valor de entrada de pelo menos US$ 5.");
      return;
    }
    if(!isValidMartingaleCount(maxGale)){setError(`Informe de 0 a ${MAX_MARTINGALES} martingales.`);return;}
    if(!isValidLossLimit(lossLimit)){setError('Informe um limite de perdas válido.');return;}
    if (!status?.available || !status?.connected || !status.demo_confirmed || status.auto?.running || status.auto?.phase === "unknown") return;
    if (mode === "manual") {
      perform(() => post("/independent/manual/scan", {signalPolicy,mercado:'all'}));
      return;
    }
    perform(() => post("/independent/auto/start", {
      account_mode: "demo",
      amount: value,
      maxGale,
      mercado: "all",
      timeframe: "M1",
      signalPolicy,maxConsecutiveLosses:lossLimit,
    }));
  };

  const auto = status?.auto;
  const running = auto?.running === true;
  const signal = auto?.signal?.winner ?? auto?.signal;
  const indicators = signal?.indicadores ?? signal?.indicators;
  const scoreExplanation=explainIndependentScore(signal);
  const entry = auto?.signal?.planned_entry_timestamp ?? signal?.entry_timestamp ?? signal?.entrada_timestamp ?? signal?.entry_at;
  const order = auto?.current_order;
  const result = auto?.last_result;
  const outcome = result?.outcome ?? result?.result;
  const numericAmount = Number(amount);
  const displayedAmount = running ? auto.config?.amount ?? numericAmount : numericAmount;
  const displayedGale = running ? auto.config?.maxGale ?? maxGale : maxGale;
  const locked = running || status?.connecting || ["open", "submitting", "unknown"].includes(auto?.phase);
  const canStart = status?.available === true && status?.connected === true && status.demo_confirmed === true && !locked && !busy;
  const canConfirm = canStart && mode === "manual" && auto?.phase === "manual_ready" && (auto.config?.signalPolicy??BASELINE_POLICY)===signalPolicy && entry * 1000 > currentTime && numericAmount >= 5 && isValidMartingaleCount(maxGale);

  return (
    <section className="card local-generator-card" aria-labelledby="independent-auto-heading">
      <div className="generator-introduction">
      <span className="eyebrow">OPORTUNIDADES · M1 · ABERTO + OTC</span>
      <h2 id="independent-auto-heading">Gerador local · Demo</h2>
      <p className="muted">Dados diretos Polarium · algoritmo local experimental.</p>
      <p className="muted">A busca automática e as entradas Demo rodam no servidor, mesmo com a página fechada ou após sair do painel. Use “Parar busca” para interromper novas entradas.</p>
      </div>
      <div className="generator-field">
      <label htmlFor="independent-mode">Modo de operação</label>
      <select id="independent-mode" value={mode} disabled={busy || locked} onChange={event => {
        setMode(event.target.value);
        localStorage.setItem("trader.generator.mode", event.target.value);
      }}>
        <option value="automatic">Automático</option>
        <option value="manual">Manual</option>
      </select>
      <p className="generator-mode-description">
        {mode === "automatic" ? <>Analisa todos os ativos em cada fechamento de vela e entra em todos os aprovados na próxima vela. A busca continua enquanto acompanha cada operação.</> : <>Você aciona a busca e confirma o lote de ativos listados. Cada ativo é revalidado antes da entrada. Após concluir as operações e os martingales autorizados, o sistema aguarda uma nova busca manual.</>}
      </p>
      </div>

      <div className="generator-connection-status" role="status" aria-live="polite">
        <strong>{status?.connected ? "Polarium conectada" : "Polarium desconectada"}</strong>
        <div>{status?.demo_confirmed ? "Conta Demo confirmada" : "Conta Demo aguardando confirmação"}</div>
        <div>{phases[auto?.phase] ?? (auto?.phase || "Aguardando conexão")}</div>
        {auto?.message ? <div>{auto.message}</div> : null}
        {status?.reason ? <div>{status.reason}</div> : null}
        {status?.warming ? <div>Preparando ativos: {status.warmed_assets} de {status.total_assets}</div> : null}
        {auto?.next_scan_at ? <div>Próxima busca: {dateTime(auto.next_scan_at)}</div> : null}
        {auto?.phase === "waiting_entry" && auto.planned_entry_timestamp ? <div>Entrada G{auto.gale??0} agendada: {dateTime(auto.planned_entry_timestamp)}</div> : null}
      </div>
      {auto?.phase==='unknown'&&!running?<div className="generator-field">
        <button type="button" className="secondary" disabled={busy} onClick={()=>perform(()=>post('/independent/auto/recover'))}>Liberar novas buscas após expiração</button>
        <small>Disponível para envio incerto Demo sem identificador, um minuto após a expiração. Preserva a ordem sem resultado confirmado e não a reenvia.</small>
      </div>:null}

      {status?.connected ? (
        <button type="button" className="secondary" disabled={busy || locked} onClick={() => perform(() => api("/independent/connect", { method: "DELETE" }))}>
          Desconectar Polarium
        </button>
      ) : (
        <div className="generator-connection">
        <form onSubmit={login} className="generator-form" aria-label="Login Polarium">
          <h3>Conectar à Polarium</h3>
          <div className="generator-field"><label htmlFor="polarium-email">E-mail da Polarium</label>
          <input id="polarium-email" type="email" autoComplete="username" required value={email} onChange={event=>setEmail(event.target.value)} disabled={busy||locked} placeholder="Seu e-mail na corretora"/></div>
          <div className="generator-field"><label htmlFor="polarium-password">Senha da Polarium</label>
          <div className="generator-password"><input id="polarium-password" type={showPassword?'text':'password'} autoComplete="current-password" required value={password} onChange={event=>setPassword(event.target.value)} disabled={busy||locked} placeholder="Sua senha na corretora"/>
          <button type="button" aria-label={showPassword?'Ocultar senha Polarium':'Mostrar senha Polarium'} onClick={()=>setShowPassword(value=>!value)}>{showPassword?<EyeOff size={18}/>:<Eye size={18}/>}</button></div></div>
          <button type="submit" className="secondary" disabled={busy||locked||!email.trim()||!password}>{busy?'Conectando…':'Entrar e conectar Demo'}</button>
          <small className="generator-security-note"><ShieldCheck size={16}/><span>A senha não é gravada. O login cria a sessão e não envia ordens.</span></small>
        </form>
        <details className="generator-advanced"><summary>Conexão avançada por sessão</summary>
        <p>Use apenas se a corretora exigir uma verificação adicional no login.</p>
        <a href="https://trade.polariumbroker.com/pt/login" target="_blank" rel="noreferrer">Abrir login da Polarium</a>
        <form onSubmit={connect} className="generator-form">
          <label htmlFor="independent-ssid">Sessão Polarium (SSID)</label>
          <input id="independent-ssid" type="password" autoComplete="off" value={ssid} onChange={(event) => setSsid(event.target.value)} disabled={busy} placeholder="Sessão autenticada da Polarium" />
          <details><summary>Dados adicionais da sessão</summary><label htmlFor="independent-feature-identity">Identificador de recursos (opcional)</label><input id="independent-feature-identity" type="password" autoComplete="off" value={featureIdentity} onChange={event=>setFeatureIdentity(event.target.value)} disabled={busy}/></details>
          <button type="submit" className="secondary" disabled={busy || locked || !ssid.trim()}>Conectar sessão Demo</button>
          <small>Conectar a sessão não inicia a busca nem envia ordens.</small>
        </form>
        </details></div>
      )}

      <form onSubmit={start} className="generator-form generator-operation-form">
        <h3>Configurar entradas</h3>
        <div className="generator-field"><b>Confluência + EMA35 · obrigatória</b>
        <small>Alta permite somente CALL; baixa, somente PUT. A tendência exige inclinação e movimento dos fechamentos das 15 velas na mesma direção. Também exige RSI alinhado, diferença DI ≥ 10, ADX ≥ 25, duas últimas velas na direção, avanço dos fechamentos e movimento direcional recente alinhado. Um filtro reprovado bloqueia a entrada. Não há aumento de acerto comprovado.</small></div>
        {(running?auto.config?.signalPolicy:signalPolicy)===EMA_CLOCK_POLICY?<div className="generator-score-explanation"><b>EMA35 · filtro do relógio</b>
          <div>CALL: 12h a 2h (30° a 90°). PUT: 3h30 a 5h (−60° a −15°).</div>
          <small>EMA exponencial de 35 períodos. Inclinação média em 5 velas fechadas, normalizada pelo ATR14. Um ATR por vela é a escala fixa; o zoom não altera o resultado. Histórico necessário: 180 velas M1 fechadas.</small>
        </div>:null}
        <div className="generator-field">
        <label htmlFor="independent-amount">Valor por ativo em Demo (US$)</label>
        <input id="independent-amount" type="number" min="5" step="0.01" required value={running ? auto.config?.amount ?? amount : amount} disabled={busy || running} onChange={(event) => setAmount(event.target.value)} />
        <small>Todos os ativos aprovados recebem entrada na próxima vela, inclusive em caso de empate. Com 20 aprovados e US$5 por ativo, são US$100 em entradas iniciais. Cada ativo mantém sua própria operação.</small>
        </div><div className="generator-field">
        <label htmlFor="independent-gale">Quantidade de martingales após LOSS</label>
        <input id="independent-gale" type="number" min="0" max={MAX_MARTINGALES} step="1" required value={displayedGale} disabled={busy || locked} onChange={event=>{
          const value=event.target.value===''?'':Number(event.target.value);setMaxGale(value);
          if(isValidMartingaleCount(value))localStorage.setItem('trader.generator.maxGale',String(value));
        }}/>
        <small>0 desativa. Padrão: 1. Máximo: {MAX_MARTINGALES}.</small>
        </div><div className="generator-amount-summary">
          Entrada: {Number.isFinite(displayedAmount) && displayedAmount > 0 ? dollarFormatter.format(displayedAmount) : "—"}
          {isValidMartingaleCount(displayedGale)&&displayedGale>0?Array.from({length:displayedGale},(_,i)=><div key={i}>G{i+1}: {Number.isFinite(displayedAmount)&&displayedAmount>0?dollarFormatter.format(martingaleAmount(displayedAmount,i+1)):'—'}</div>):' · Sem martingale'}
        </div>
        <small style={{ lineHeight: 1.6 }}>
          Após LOSS confirmado, o martingale repete o mesmo ativo e direção na vela seguinte, sem revalidar os indicadores. O valor dobra a cada nível autorizado. Se a confirmação chegar após os primeiros 3 segundos da vela, a recuperação é encerrada.
          {mode === "automatic" ? " A busca de novas entradas continua a cada fechamento de vela." : " Sua confirmação autoriza os ativos listados e os martingales configurados. A entrada inicial é revalidada no horário do envio."}
        </small>
        <div className="generator-field"><label htmlFor="independent-loss-limit">Pausar após ciclos consecutivos com LOSS</label>
        <input id="independent-loss-limit" type="number" min="0" max="20" step="1" required disabled={busy||locked||mode==='manual'} value={running?auto.config?.maxConsecutiveLosses??0:lossLimit} onChange={event=>{
          const value=event.target.value===''?'':Number(event.target.value);setLossLimit(value);
          if(isValidLossLimit(value))localStorage.setItem('trader.generator.lossLimit',String(value));
        }}/><small>0 desativa. Conta somente o resultado final após os martingales. WIN ou DOJI zera a sequência. Ao atingir o limite, a busca pausa e exige novo início.</small>
        {running?<small>Sequência atual: {auto.consecutive_losses??0} ciclos com LOSS.</small>:null}</div>
        <button type="submit" className="primary" disabled={!canStart}>{mode === "manual" ? "Buscar oportunidade" : "Iniciar busca e entradas Demo"}</button>
        {mode === "manual" && auto?.phase === "manual_ready" ? <>
          <button type="button" className="primary" disabled={!canConfirm} onClick={() => perform(() => post("/independent/manual/confirm", {
            signal_id: signal.cycle_id, account_mode: "demo", amount: numericAmount, maxGale, signalPolicy,mercado: auto.config?.mercado??'all', timeframe: "M1",
          }))}>Confirmar entradas Demo ({auto.signals?.length??1} ativos)</button>
          <small>{entry * 1000 > currentTime ? "A entrada será enviada no horário do sinal somente após sua confirmação." : "Sinal vencido. Busque uma nova oportunidade."}</small>
        </> : null}
        <button type="button" className="secondary" disabled={busy || !running} onClick={() => perform(() => post("/independent/auto/stop"))}>Solicitar parada</button>
        <small>A parada impede novas entradas. Uma ordem já enviada continua aguardando liquidação.</small>
      </form>

      {error || pollError ? <p className="generator-error" role="alert">{error || pollError}</p> : null}
      {auto?.signals?.length?<div className="generator-score-explanation"><b>Ativos aprovados na última análise: {auto.signals.length}</b>
        <ul>{auto.signals.map(row=><li key={row.active_id}>{row.ativo} · {row.direcao==='CALL'?'Compra':'Venda'} · EMA35 {row.ma35?.clock??'—'}</li>)}</ul>
      </div>:null}
      {auto?.positions?.length?<div className="generator-score-explanation"><b>Operações por ativo</b>
        <ul>{auto.positions.map(position=><li key={position.cycle_id}>{position.signal?.ativo} · {position.signal?.direcao==='CALL'?'Compra':'Venda'} · G{position.current_order?.gale??0} · {position.current_order?.result??phases[position.phase]??position.phase}
          {position.current_order?.amount!=null?` · ${dollarFormatter.format(position.current_order.amount)}`:''}
          <div>{position.message}</div></li>)}</ul>
      </div>:null}
      {signal ? (
        <div style={{ marginTop: 20, lineHeight: 1.7 }}>
          <h3>Oportunidade local</h3>
          <strong>{signal.ativo ?? signal.asset ?? "Ativo não confirmado"} · {signal.direcao ?? signal.direction ?? "Direção não confirmada"}</strong>
          <div>Horário da entrada: {dateTime(entry)}</div>
          <div>Pontuação de seleção: {metric(signal.confirmacao ?? signal.confidence)} pontos</div>
          {scoreExplanation?<div className="generator-score-explanation">
            <div><b>Consistência das velas: {metric(scoreExplanation.consistency,'%')}</b></div>
            <div>{scoreExplanation.aligned} de {scoreExplanation.total} velas {scoreExplanation.color} na direção {signal.direcao}.</div>
            <div>{scoreExplanation.counts.verdes} verdes · {scoreExplanation.counts.vermelhas} vermelhas · {scoreExplanation.counts.cinzas} dojis.</div>
            <div>Cálculo: {scoreExplanation.aligned} ÷ {scoreExplanation.total} × 100 + {scoreExplanation.bonus} = {metric(scoreExplanation.points)} pontos.</div>
            <small>O acréscimo de 5 pontos é fixo na regra experimental. RSI e DI são filtros; ADX desempata a seleção e também é obrigatório no perfil seletivo. Essa pontuação não representa probabilidade de vitória.</small>
          </div>:<small>A composição desta pontuação não foi confirmada neste registro.</small>}
          <div>RSI: {metric(indicators?.rsi)} · ADX: {metric(indicators?.adx)}</div>
          <div>+DI: {metric(indicators?.plus_di ?? indicators?.pdi)} · −DI: {metric(indicators?.minus_di ?? indicators?.mdi)}</div>
          {signal.ma35?<div>EMA35: {metric(signal.ma35.ema)} · ângulo: {metric(signal.ma35.angle_degrees,'°')} · relógio: {signal.ma35.clock??'—'}</div>:null}
        </div>
      ) : null}
      {order ? (
        <div style={{ marginTop: 16, lineHeight: 1.7 }}>
          <h3>Entrada Demo · G{order.level??order.gale??0}</h3>
          <div>Estado: {phases[order.phase] ?? order.status ?? "Aguardando confirmação"}</div>
          {order.definitely_not_sent===true?<div>Nenhuma ordem foi enviada.</div>:null}
          {order.order_id != null ? <div>Identificador: {String(order.order_id)}</div> : null}
          {order.amount != null ? <div>Valor: {typeof order.amount === "number" && Number.isFinite(order.amount) ? dollarFormatter.format(order.amount) : "Não confirmado"}</div> : null}
          {order.entry_timestamp ? <div>Entrada: {dateTime(order.entry_timestamp)}</div> : null}
          {order.expiration_timestamp ? <div>Expiração: {dateTime(order.expiration_timestamp)}</div> : null}
        </div>
      ) : null}
      {auto?.last_cycle?<div className="generator-form"><h3>Último ciclo concluído</h3><strong>{auto.last_cycle.result}</strong>
        <div>{auto.last_cycle.ativo} · finalizado em G{auto.last_cycle.gale}</div><div>{dateTime(auto.last_cycle.settled_at)}</div></div>:null}
      {result ? (
        <details className="generator-form generator-advanced">
          <summary>Última liquidação por ordem · G{result.level??result.gale??0}</summary>
          <div>{result.confirmed === true && ["WIN", "LOSS", "DOJI"].includes(outcome) ? outcome : "Resultado não confirmado"}</div>
          {result.confirmed === true ? <div>G{result.level??result.gale??0} · liquidação confirmada pela Polarium</div> : null}
          {result.settled_at ? <div>{dateTime(result.settled_at)}</div> : null}
        </details>
      ) : null}
      <TradeReviewPanel/>
      {auto?.last_scan?<div className="generator-score-explanation"><b>Última análise dos ativos</b>
        <div>Início: {auto.last_scan.started_at?dateTime(auto.last_scan.started_at):'Não registrado'}</div>
        <div>Término: {auto.last_scan.finished_at?dateTime(auto.last_scan.finished_at):'Não registrado'}</div>
        <div>{auto.last_scan.evaluated??0} de {auto.last_scan.expected??0} ativos avaliados.</div>
        {Number.isInteger(auto.last_scan.fully_evaluated)?<div>Indicadores e filtros completos: {auto.last_scan.fully_evaluated}/{auto.last_scan.expected??0} · Aprovados: {auto.last_scan.fully_approved??0}</div>:null}
        {auto.last_scan.markets?<div>Aberto: {auto.last_scan.markets.open?.evaluated??0}/{auto.last_scan.markets.open?.expected??0} · OTC: {auto.last_scan.markets.otc?.evaluated??0}/{auto.last_scan.markets.otc?.expected??0}</div>:null}
        {Number.isFinite(auto.last_scan.duration_ms)?<div>Tempo da análise: {(auto.last_scan.duration_ms/1000).toLocaleString('pt-BR',{maximumFractionDigits:1})} s</div>:null}
        <small>As novas buscas preservam indicadores, filtros, ranking e 15 velas por ativo no servidor para comparar estratégias.</small>
        {auto.last_scan.filter_reason?<div>{auto.last_scan.filter_reason}</div>:null}
        {auto.last_scan.ma35?<div>EMA35: {auto.last_scan.ma35.valid?`${metric(auto.last_scan.ma35.angle_degrees,'°')} · relógio ${auto.last_scan.ma35.clock}`:auto.last_scan.ma35.reason}</div>:null}
        {auto.last_scan.quality_gates?<ul>{Object.entries(auto.last_scan.quality_gates).map(([key,passed])=><li key={key}>{passed?'✓':'✕'} {SELECTIVE_GATE_LABELS[key]??key}: {passed?'OK':'reprovado'}</li>)}</ul>:null}
      </div>:null}
    </section>
  );
}
